import React, { useEffect, useState } from "react";
import LeavePage from "../../entities/LeavePage";
import AuthService from "../../api/AuthService";
import LoaderHelper from "../../utils/Loader/LoaderHelper";
import { useAlert } from "../../context/AlertContext";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";

const Leave = () => {
  const [leave, setLeave] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [data, setData] = useState();
  const [financialYearOptions, setFinancialYearOptions] = useState([]);
  const [country, setCountry] = useState("");
  const [allLeaveTypes, setAllLeaveTypes] = useState([]);
  const [selectedYears, setSelectedYears] = useState(null);
  const location = useLocation();
  const taxCountry = location?.state?.taxCountry;

  const { showAlert } = useAlert();

  useEffect(() => {
    getLeaveBalanceRate();
    getCompanyCountry();
    fetchAllLeaveTypesForAdmin();
  }, []);

  useEffect(() => {
    if (taxCountry || country) {
      getTaxYear(taxCountry || country);
    }
  }, [taxCountry, country]);


  useEffect(() => {
    if (financialYearOptions.length > 0) {
      setSelectedYears(financialYearOptions[0]);
      getLeave(financialYearOptions[0].start, financialYearOptions[0].end, taxCountry);
    }
  }, [financialYearOptions, taxCountry]);

  const fetchAllLeaveTypesForAdmin = async () => {
    try {
      const result = await AuthService.getAllLeaveTypeForAdmin(taxCountry);
      if (result?.success) {
        setAllLeaveTypes(result?.data || []);
      } else {
        setAllLeaveTypes([]);
      }
    } catch (error) {
      setAllLeaveTypes([]);
    }
  };

  const handleYearChange = (selectedOption) => {
    setSelectedYears(selectedOption);
    getLeave(selectedOption.start, selectedOption.end, taxCountry);
  };

  const getLeave = async (start, end, taxCountry) => {
    LoaderHelper.loaderStatus(true);
    try {
      const selStart = start || selectedYears?.start || financialYearOptions?.[0]?.start || "";
      const selEnd = end || selectedYears?.end || financialYearOptions?.[0]?.end || "";
      const selTax = typeof taxCountry !== "undefined" && taxCountry !== null ? taxCountry : (location?.state?.taxCountry || "");
      const result = await AuthService.getAdmLeave(selStart, selEnd, selTax);
      if (result?.success) {
        const leaveData = result?.data?.leaveSettings || result?.data;
        if (Array.isArray(leaveData)) {
          setLeave(leaveData);
        } else if (leaveData && Array.isArray(leaveData.data)) {
          setLeave(leaveData.data);
        } else {
          setLeave([]);
        }
      } else {
        setLeave([]);
      }
    } catch (error) {
      setLeave([]);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getLeaveBalanceRate = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getLeaveBalanceRate();
      if (result?.success) {
        setData(result?.data[0]);

        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };
  const handleLeave = async (payload, id) => {
    const result = await AuthService.handleSupLeave(payload, id);
    if (result?.success === true) {
      return result;
    } else {
      showAlert(result?.message || "Update failed", "error");
      throw new Error(result?.message || "Update failed");
    }
  };

  const handleCreateLeave = async (payload) => {
    const result = await AuthService.createSupLeave(payload);
    if (result?.success === true) {
      return result;
    } else {
      showAlert(result?.message || "Create failed", "error");
      throw new Error(result?.message || "Create failed");
    }
  };

  const getUpdatedLeaveTypeFromResponse = (result, fallbackId, fallbackType) => {
    const data =
      result?.data?.leaveType ||
      result?.data?.adminLeaveType ||
      result?.data?.data ||
      result?.data ||
      {};

    return {
      ...data,
      _id: data?._id || data?.id || fallbackId,
      type: data?.type || data?.typeName || data?.name || fallbackType,
    };
  };

  const applyLeaveTypeUpdateToState = (updatedLeaveType) => {
    if (!updatedLeaveType?._id || !updatedLeaveType?.type) return;

    setAllLeaveTypes((prev) => {
      if (!Array.isArray(prev)) return prev;

      return prev.map((item) =>
        item?._id === updatedLeaveType._id
          ? { ...item, ...updatedLeaveType, type: updatedLeaveType.type }
          : item
      );
    });

    setLeave((prev) => {
      if (!Array.isArray(prev)) return prev;

      return prev.map((leaveItem) => {
        const leaveType = leaveItem?.type;
        const leaveTypeId =
          typeof leaveType === "object" ? leaveType?._id : leaveType;

        if (leaveTypeId !== updatedLeaveType._id) return leaveItem;

        return {
          ...leaveItem,
          type:
            typeof leaveType === "object"
              ? { ...leaveType, ...updatedLeaveType, type: updatedLeaveType.type }
              : updatedLeaveType.type,
        };
      });
    });
  };

  const handleAddLeaveRate = async (rate, id) => {
    setLoading(true);
    try {
      const result = await AuthService.addLeaveRate(rate, id);
      if (result?.success === true) {
        setLoading(false);
        getLeaveBalanceRate();
        showAlert(result?.message, "success");
      } else {
        setLoading(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      setLoading(false);
      showAlert(error?.message, "error");
    }
  };

  const handleDeleteLeave = async (id) => {
    setLoading(true);
    try {
      const result = await AuthService.deleteLeave(id);
      if (result?.success === true) {
        await getLeave();
        await fetchAllLeaveTypesForAdmin();
        setLoading(false);
        showAlert(result?.message, "success");
      } else {
        setLoading(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      setLoading(false);
      showAlert(error?.message, "error");
    }
  };

  const handleDeleteAdminLeaveType = async (id) => {
    setLoading(true);
    try {
      const result = await AuthService.deleteAdminLeaveType(id);
      if (result?.success === true) {
        await getLeave();
        await fetchAllLeaveTypesForAdmin();
        setLoading(false);
        showAlert(result?.message || "Leave type deleted successfully", "success");
      } else {
        setLoading(false);
        showAlert(result?.message || "Delete failed", "error");
      }
    } catch (error) {
      setLoading(false);
      showAlert(error?.message || "Delete failed", "error");
    }
  };

  const addLeaveType = async (newButtonLabel) => {
    setLoading(true);
    try {
      const result = await AuthService.handleLeaveType(newButtonLabel, taxCountry);
      if (result?.success === true) {
        showAlert(result?.message || "Leave type added successfully", "success");
        await getLeave();
        await fetchAllLeaveTypesForAdmin();
        return result;
      } else {
        showAlert(result?.message || "Add leave type failed", "error");
        throw new Error(result?.message || "Add leave type failed");
      }
    } catch (error) {
      showAlert(error?.message || "Add leave type failed", "error");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateLeaveType = async (id, newButtonLabel) => {
    setLoading(true);
    try {
      const result = await AuthService.updateAdminLeaveType(id, newButtonLabel, taxCountry);
      if (result?.success === true) {
        const updatedLeaveType = getUpdatedLeaveTypeFromResponse(
          result,
          id,
          newButtonLabel
        );

        applyLeaveTypeUpdateToState(updatedLeaveType);
        showAlert(result?.message || "Leave type updated successfully", "success");
        await getLeave();
        await fetchAllLeaveTypesForAdmin();
        applyLeaveTypeUpdateToState(updatedLeaveType);
        return result;
      } else {
        showAlert(result?.message || "Update leave type failed", "error");
        throw new Error(result?.message || "Update leave type failed");
      }
    } catch (error) {
      showAlert(error?.message || "Update leave type failed", "error");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getTaxYear = async (countryForTaxPeriod) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getTaxPeriod(countryForTaxPeriod);
      if (result?.success) {
        // Format the tax period data to show start/end dates
        const formattedYears = result?.data?.map((year) => {
          const start = dayjs(year.start).format("YYYY");
          const end = dayjs(year.end).format("YYYY");
          return {
            value: `${start}/${end}`,
            label: `${start}/${end}`,
            id: year._id,
            start: year.start,
            end: year.end,
          };
        });
        setFinancialYearOptions(formattedYears || []);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getCompanyCountry = async () => {
    try {
      const result = await AuthService.getAccountSetting();

      if (result?.success && result?.data?.country) {
        setCountry(result.data.country);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  return (
    <>
      <LeavePage
        leaves={leave}
        data={data}
        allLeaveTypes={allLeaveTypes}
        handleAddLeaveRate={handleAddLeaveRate}
        handleLeave={handleLeave}
        handleCreateLeave={handleCreateLeave}
        handleDeleteLeave={handleDeleteLeave}
        handleDeleteAdminLeaveType={handleDeleteAdminLeaveType}
        addLeaveType={addLeaveType}
        updateLeaveType={updateLeaveType}
        loading={loading}
        isPaid={isPaid}
        setIsPaid={setIsPaid}
        financialYearOptions={financialYearOptions}
        country={country}
        onSaveComplete={async () => {
          await getLeave();
          await fetchAllLeaveTypesForAdmin();
        }}
        setLoading={setLoading}
        showAlert={showAlert}
        selectedYears={selectedYears}
        handleYearChange={handleYearChange}
        taxCountry={taxCountry}
      />
    </>
  );
};

export default Leave;
